// Eskom PARCC — pure logic for the Working Paper capture/edit save.
//
// React-free, Firebase-free helpers so they unit-test in isolation. The Working
// Papers editor lets an assigned reviewer (or an admin) set a transaction's
// review status, working-paper notes and compliance level, then Save. This
// module builds the two write payloads that Save dispatches:
//
//   • the {@link WorkingPaperInput} for `useUpsertWorkingPaper` (the captured
//     review fields, stamped with the capturer's email), and
//   • the {@link TransactionInsertInput} for `useUpsertTransaction` — review
//     status is DUAL-WRITTEN to the transaction as well, so the register and the
//     allocation board agree with the working paper (the single rule TransactionEditor
//     also follows: reviewStatus reflects on the tx).
//
// Blank fields collapse to `undefined` so an unset control never overwrites a
// stored value with an empty string (the write layer further coerces `undefined`
// away and normalises emails).
import { normalizeEmail } from '../../lib/email';
import { rollupComplianceFromFindings, type RollupFinding } from '../../domain/gapClosure';
import { isSelfDeclaredLevel } from '../../domain/gaps';
import { reviewedFlagForStatus } from '../../domain/reviewStatus';
import type { WorkingPaperInput, TransactionInsertInput } from '../../lib/dbWrite';

/** The reviewer-editable working-paper fields the editor collects. */
export interface WorkingPaperDraft {
  reviewStatus: string;
  workingPaperNotes: string;
  complianceLevel: string;
  /** Name of the ACTUAL reviewer (owner feedback #8) — a coordinator may capture
   * on their behalf, so who reviewed it is recorded here as free text. */
  reviewerName: string;
}

/** Collapse a blank/whitespace value to `undefined` (an unset control). */
function orUndefined(value: string): string | undefined {
  const t = value.trim();
  return t === '' ? undefined : t;
}

/**
 * Build the {@link WorkingPaperInput} for `useUpsertWorkingPaper`. Carries the
 * three edited fields plus `capturedByEmail` (the signed-in reviewer, normalised
 * — blank collapses to undefined so the write layer can null it). Blank
 * status/notes/compliance collapse to `undefined`.
 */
export function buildWorkingPaperInput(
  draft: WorkingPaperDraft,
  capturedByEmail: string | null | undefined,
): WorkingPaperInput {
  const captured = normalizeEmail(capturedByEmail);
  return {
    reviewStatus: orUndefined(draft.reviewStatus),
    workingPaperNotes: orUndefined(draft.workingPaperNotes),
    complianceLevel: orUndefined(draft.complianceLevel),
    reviewerName: orUndefined(draft.reviewerName),
    capturedByEmail: captured === '' ? undefined : captured,
  };
}

/**
 * Build the {@link TransactionInsertInput} that mirrors the review status onto
 * the transaction (the dual-write). Writes BOTH review signals in lock-step:
 * `reviewStatus` (the string the WP/Findings/Allocation surfaces read) AND the
 * boolean `reviewed` (what the register/detail read via `isReviewed`) — derived
 * from the status by {@link reviewedFlagForStatus}. Without the boolean, setting
 * "Not Reviewed" here left `Transaction.reviewed` true, so the register kept
 * showing the row Reviewed (the Jul 8 "un-review won't stick" bug). No other
 * transaction column is disturbed. Returns `null` when the draft has no review
 * status to write — nothing to mirror, and an empty upsert would bump the row.
 */
export function buildReviewStatusTxPatch(
  referenceNo: string,
  draft: WorkingPaperDraft,
): TransactionInsertInput | null {
  const status = orUndefined(draft.reviewStatus);
  if (status === undefined) return null;
  return { referenceNo, reviewStatus: status, reviewed: reviewedFlagForStatus(status) };
}

/**
 * Resolve the compliance level a save should persist (R3 #2 propagation).
 * The reviewer's EXPLICIT choice wins (they changed the select this session).
 * Otherwise, when the finding lines were EDITED this session (`findingsChanged`), the
 * level is RECOMPUTED from their lifecycle states via {@link rollupComplianceFromFindings}
 * — so closing a finding's gap updates the transaction, dashboard and reports without a
 * manual compliance edit. A save that touched neither the select nor a line (notes,
 * review status, reviewer name) is a no-op on the verdict: the seeded level stands.
 *
 * Why the "lines edited" gate (29 Sep 2026, Lindiwe: 4600080858 set to Compliant "on both
 * my laptop and Nozipho's one" still read self-declared): the seeded level carries no
 * provenance, so an untouched save used to re-derive it from the KEPT self-declared lines
 * and silently write the verdict back — and would do the same to a restored 'To delete'.
 * Two further guards where the roll-up would contradict an explicit verdict:
 *  • a seeded lifecycle sentinel ('Deleted' / 'To delete') is undone only by the select;
 *  • a self-declared roll-up never DOWNGRADES a seeded 'Compliant' — under the closure
 *    model a self-declared finding is a CLOSED finding (kept, tier retained, reported
 *    separately) and self-declaration is decided at transaction grain (16 Aug), so the
 *    reviewer picks the Self-Declared level to declare the transaction. A live gap (an
 *    open line → a plain tier) still moves a Compliant paper.
 */
export function resolveComplianceForSave(
  draftCompliance: string,
  seededCompliance: string,
  findings: readonly RollupFinding[],
  findingsChanged: boolean,
): string {
  if (draftCompliance.trim() !== seededCompliance.trim()) return draftCompliance;
  if (!findingsChanged) return draftCompliance;
  const seeded = seededCompliance.trim();
  if (seeded === 'Deleted' || seeded === 'To delete') return draftCompliance;
  const rolled = rollupComplianceFromFindings(findings);
  if (rolled === undefined) return draftCompliance;
  if (seeded === 'Compliant' && isSelfDeclaredLevel(rolled)) return draftCompliance;
  return rolled;
}

/**
 * Build the {@link TransactionInsertInput} that mirrors the resolved compliance
 * onto the transaction — dual-written as both `complianceLevel` and the
 * strict-rule `complianceLevelOverride` (what the register's effectiveCompliance
 * reads as authoritative), so every surface agrees after a gap closes. Returns
 * `null` for a blank compliance (nothing to mirror).
 */
export function buildComplianceTxPatch(
  referenceNo: string,
  compliance: string,
): TransactionInsertInput | null {
  const level = orUndefined(compliance);
  if (level === undefined) return null;
  return { referenceNo, complianceLevel: level, complianceLevelOverride: level };
}
